import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  Loader2,
  MessageCircleQuestion,
  Send,
  UserRoundPlus,
  X,
} from 'lucide-react';

import {
  closeQuestion,
  getQuestionThread,
  listQuestions,
  listTransferTargets,
  replyToQuestion,
  transferQuestion,
} from '../../services/agentQuestionsApi';
import { confirmDialog } from '../ConfirmDialog';
import { listTime } from '../../utils/helper';

const MAX_CHARS = 2000;
const SOURCE = { meeting: 'Meeting', mail: 'Mail' };

/** The member's open questions; `reload` after anything that changes them. */
export const useAgentQuestions = (accessToken) => {
  const [questions, setQuestions] = useState([]);
  const reload = useCallback(() => {
    if (!accessToken) return Promise.resolve();
    return (
      listQuestions(accessToken)
        .then((res) => setQuestions(res?.data || []))
        // Non-fatal: the Waiting tab still shows its meetings.
        .catch(() => {})
    );
  }, [accessToken]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { questions, reload };
};

/** One question in the Waiting list (always above the meetings). */
export const QuestionItem = ({ question, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/60"
  >
    <MessageCircleQuestion className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#8b88f0]" />
    <span className="min-w-0 flex-1">
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium text-zinc-200">
          {question.title}
        </span>
        {question.updated_at && (
          <span className="flex-shrink-0 text-[10px] text-zinc-500">
            {listTime(question.updated_at)}
          </span>
        )}
      </span>
      <span className="mt-0.5 block truncate text-[11px] text-zinc-400">
        {question.question}
      </span>
      <span className="mt-1 flex flex-wrap gap-1">
        <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
          {SOURCE[question.source] || 'Question'}
        </span>
        {['high', 'HIGH'].includes(question.urgency) && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
            Urgent
          </span>
        )}
        {question.unsure && (
          <span
            title="The pipeline could not tell who should answer: answer it or pass it on"
            className="rounded bg-zinc-700/60 px-1.5 py-0.5 text-[10px] text-zinc-300"
          >
            Not sure who should answer
          </span>
        )}
        {question.transferred_by && (
          <span className="truncate rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
            From {question.transferred_by}
          </span>
        )}
        {question.replies > 0 && (
          <span className="rounded bg-[#5652CF]/20 px-1.5 py-0.5 text-[10px] text-indigo-200">
            In conversation
          </span>
        )}
      </span>
    </span>
  </button>
);

/** One line of a question's history: who did what, for tracing it later. */
export const historyLine = (e) => {
  const who = e.by?.name || 'A member';
  if (e.action === 'passed')
    return `${who} passed this to ${e.to?.name || 'a member'}`;
  if (e.action === 'answered') return `${who} answered: ${e.resolution}`;
  if (e.action === 'answered_with_related')
    return `${who} answered it with a related question: ${e.resolution}`;
  if (e.action === 'closed') return `${who} closed it for themselves`;
  return `${who}: ${e.action}`;
};

const Bubble = ({ role, children }) => (
  <li
    className={`flex flex-col ${role === 'user' ? 'items-end' : 'items-start'}`}
  >
    {role !== 'user' && (
      <span className="mb-0.5 flex items-center gap-1 text-[10px] text-zinc-500">
        <Bot className="h-3 w-3" /> SuperManager
      </span>
    )}
    <div
      className={`max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-3 py-1.5 text-[13px] text-zinc-100 ${
        role === 'user' ? 'bg-[#5652CF]/30' : 'bg-zinc-800'
      }`}
    >
      {children}
    </div>
  </li>
);

/** Pick a member who can read the meeting, and pass the question to them. */
const TransferPicker = ({ accessToken, questionId, onCancel, onDone }) => {
  const [people, setPeople] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    listTransferTargets(accessToken, questionId)
      .then((res) => setPeople(res?.data || []))
      .catch(() => setPeople([]));
  }, [accessToken, questionId]);

  const pass = async (person) => {
    const name = person.name || person.email;
    const ok = await confirmDialog({
      title: `Pass this question to ${name}?`,
      message:
        'It leaves your Waiting list and shows in theirs, with where it came from. The pass is kept in its history.',
      confirmLabel: 'Pass on',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await transferQuestion(accessToken, questionId, person.user_id);
      toast.success(`Passed to ${name}`);
      onDone();
    } catch (err) {
      toast.error(`Could not pass it on: ${err?.message || 'try again'}`);
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-zinc-800 px-3 py-2">
      <p className="mb-1 flex items-center justify-between text-[11px] text-zinc-400">
        Who should answer this?
        <button
          type="button"
          onClick={onCancel}
          className="text-zinc-500 hover:text-zinc-200"
        >
          Cancel
        </button>
      </p>
      {people === null && (
        <p className="text-[12px] text-zinc-500">Loading people…</p>
      )}
      {people?.length === 0 && (
        <p className="text-[12px] text-zinc-500">
          Nobody else is in this project.
        </p>
      )}
      <ul className="max-h-40 overflow-y-auto" aria-label="People">
        {(people || []).map((person) => (
          <li key={person.user_id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => pass(person)}
              className="flex w-full flex-col rounded px-2 py-1 text-left hover:bg-zinc-800 disabled:opacity-40"
            >
              <span className="text-[13px] text-zinc-100">
                {person.name || person.email}
              </span>
              {person.name && (
                <span className="text-[11px] text-zinc-500">
                  {person.email}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * A 1:1 chat with the agent about one question. A reply that answers it is
 * recorded where the question came from (the meeting or mail pipeline).
 */
export const QuestionThread = ({ accessToken, questionId, onBack, onDone }) => {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [passing, setPassing] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    let current = true;
    getQuestionThread(accessToken, questionId)
      .then((res) => current && setThread(res?.data || null))
      .catch((err) => current && setError(err || {}));
    return () => {
      current = false;
    };
  }, [accessToken, questionId]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [thread, sending]);

  const answered = thread?.status === 'answered';
  const send = async (value) => {
    const reply = (value ?? text).trim();
    if (!reply || reply.length > MAX_CHARS || sending) return;
    setSending(true);
    try {
      const res = await replyToQuestion(accessToken, questionId, reply);
      setThread(res?.data || null);
      setText('');
      if (res?.data?.status === 'answered') onDone?.();
    } catch (err) {
      // The text stays in the box so nothing typed is lost.
      toast.error(`Could not send: ${err?.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  };

  const close = async () => {
    const ok = await confirmDialog({
      title: 'Close this question?',
      message:
        'It leaves your Waiting list without an answer. It stays open in the meeting or mail it came from, where it can still be answered.',
      confirmLabel: 'Close',
    });
    if (!ok) return;
    try {
      await closeQuestion(accessToken, questionId);
      onDone?.();
      onBack();
    } catch (err) {
      toast.error(`Could not close: ${err?.message || 'try again'}`);
    }
  };

  const question = thread?.question;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-2">
        <button
          type="button"
          aria-label="Back to waiting"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-zinc-100">
          {question?.title || 'Question'}
        </span>
        {!answered && question && (
          <button
            type="button"
            onClick={() => setPassing((p) => !p)}
            title="Pass this question to someone who can answer it"
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <UserRoundPlus className="h-3.5 w-3.5" /> Pass on
          </button>
        )}
        {!answered && question && (
          <button
            type="button"
            onClick={close}
            title="Close this question for you (others can still answer it)"
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-3.5 w-3.5" /> Close
          </button>
        )}
      </div>

      {passing && (
        <TransferPicker
          accessToken={accessToken}
          questionId={questionId}
          onCancel={() => setPassing(false)}
          onDone={() => {
            onDone?.();
            onBack();
          }}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {!thread && !error && (
          <p className="py-6 text-center text-[13px] text-zinc-400">Loading…</p>
        )}
        {error && (
          <p className="py-6 text-center text-[13px] text-zinc-400">
            {error?.message || 'This question is no longer open.'}
          </p>
        )}
        {question && (
          <ul className="flex flex-col gap-2" aria-label="Conversation">
            <Bubble role="agent">
              {question.transferred_by && (
                <span className="mb-1 block text-[11px] text-zinc-400">
                  {question.transferred_by} passed this to you.
                </span>
              )}
              {question.about && (
                <span className="mb-1 block text-[11px] text-zinc-400">
                  From {question.about}
                </span>
              )}
              {question.question}
              {question.context && (
                <span className="mt-1 block text-[12px] italic text-zinc-400">
                  It came up as: “{question.context}”
                </span>
              )}
            </Bubble>
            {thread.history?.length > 0 && (
              <li aria-label="History" className="flex flex-col gap-0.5">
                {thread.history.map((e) => (
                  <span
                    key={`${e.action}-${e.at}`}
                    className="text-center text-[11px] text-zinc-500"
                  >
                    {historyLine(e)}
                    <span className="ml-1.5 text-zinc-600">
                      {listTime(e.at)}
                    </span>
                  </span>
                ))}
              </li>
            )}
            {thread.messages.map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Bubble key={i} role={m.role}>
                {m.text}
              </Bubble>
            ))}
            {sending && (
              <li className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                <Loader2 className="h-3 w-3 animate-spin" /> Reading your reply…
              </li>
            )}
          </ul>
        )}
        <div ref={end} />
      </div>

      {question && !answered && (
        <div className="shrink-0 border-t border-zinc-800 px-3 py-2">
          {question.options?.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {question.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={sending}
                  onClick={() => send(option)}
                  className="rounded-full border border-zinc-700 px-2 py-0.5 text-[11px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-40"
                >
                  {option}
                </button>
              ))}
            </div>
          )}
          <textarea
            aria-label="Your answer"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Answer, or ask what it means"
            disabled={sending}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF] disabled:opacity-60"
          />
          <div className="mt-1.5 flex justify-end">
            <button
              type="button"
              onClick={() => send()}
              disabled={!text.trim() || text.length > MAX_CHARS || sending}
              className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5" /> Send
            </button>
          </div>
        </div>
      )}
      {answered && (
        <p className="flex shrink-0 items-center justify-center gap-1.5 border-t border-zinc-800 px-3 py-2 text-[12px] text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5" /> Answered. Thanks!
        </p>
      )}
    </div>
  );
};
